import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Permanent account deletion.
 *   - User must type DELETE verbatim to arm the destructive button.
 *   - Backend blocks if wallet > €0 or there's a pending cash-out.
 *   - On success we clear local auth state, dispatch wallet-changed so the
 *     sidebar refreshes, then navigate to /login.
 */
export default function DeleteAccountDialog({ open, onOpenChange, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const canDelete = confirmText.trim() === "DELETE" && !busy;

  const submit = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete("/auth/me", { data: { confirm: "DELETE" } });
      toast.success("Your account has been deleted");
      // Local cleanup — wipe any cached auth tokens so the next page load
      // doesn't flash a stale logged-in state.
      try {
        localStorage.removeItem("notelosh_token");
        localStorage.removeItem("notelosh_user");
      } catch { /* ignore */ }
      onDeleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not delete account";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { setConfirmText(""); onOpenChange(v); } }}>
      <DialogContent
        data-testid="delete-account-dialog"
        className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-[#F87171]">
            <AlertTriangle className="h-4 w-4" /> Delete your account
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-[#7F1D1D]/40 bg-[#1E0A0A] p-3 text-sm text-[#FCA5A5] space-y-1.5">
            <p className="font-medium text-[#FCA5A5]">This cannot be undone.</p>
            <ul className="text-[12.5px] leading-relaxed text-[#FCA5A5]/90 list-disc pl-4 space-y-0.5">
              <li>All your notes, links and files will be deleted</li>
              <li>Your marketplace listings will be removed from the shop</li>
              <li>Your reminders, votes and comments will be deleted</li>
              <li>Buyers who already bought your notes keep their copy</li>
              <li>If your wallet has money in it, cash out first — we won't let the balance vanish</li>
            </ul>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="delete-confirm" className="text-xs text-[#9CA3AF]">
              Type <span className="text-[#F3F4F6] font-mono">DELETE</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              data-testid="delete-account-confirm-input"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl font-mono tracking-widest"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            data-testid="delete-account-cancel-btn"
            onClick={() => { setConfirmText(""); onOpenChange(false); }}
            disabled={busy}
            variant="ghost"
            className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="delete-account-submit-btn"
            onClick={submit}
            disabled={!canDelete}
            className="rounded-full bg-[#B91C1C] hover:bg-[#991B1B] text-white gap-1.5 disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete my account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
